import * as React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { ChevronDown, LogOut, Menu, Search, Settings, UserRound } from "Components/ui/icons";

import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EnvBadge,
  IconButton,
  Kbd,
  notify,
  ThemeToggle,
} from "Components/ui";
import { findNavLocation } from "config/navigation";
import { useAuth } from "hooks/useAuth";
import { avatarSrc } from "utils/avatar";
import { withProfile } from "utils/profileSheet";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

interface TopbarProps {
  onOpenMenu: () => void;
  onOpenSearch: () => void;
}

export const Topbar: React.FC<TopbarProps> = ({ onOpenMenu, onOpenSearch }) => {
  const location = useLocation();
  const { pathname } = location;
  const history = useHistory();
  const { user, signOut } = useAuth();
  const here = findNavLocation(pathname);
  const name = user?.name || "Signed in";

  const handleSignOut = () => {
    signOut();
    notify.success("Signed out");
    history.push("/credentials");
  };

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-background/85 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-6 lg:px-8">
      <IconButton
        label="Open navigation"
        icon={<Menu />}
        onClick={onOpenMenu}
        className="-ml-2 lg:hidden"
      />

      {here && (
        <p className="flex min-w-0 items-center gap-2 truncate text-sm">
          <span className="hidden text-muted-foreground sm:inline">{here.group.label}</span>
          <span aria-hidden="true" className="hidden text-subtle-foreground sm:inline">
            /
          </span>
          <span className="truncate font-medium text-foreground">{here.item.label}</span>
        </p>
      )}

      <div className="ml-auto flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onOpenSearch}
          leftIcon={<Search />}
          className="hidden w-56 justify-start text-muted-foreground md:inline-flex"
        >
          <span className="flex-1 text-left font-normal">Search…</span>
          <Kbd>{isMac ? "⌘" : "Ctrl"} K</Kbd>
        </Button>
        <IconButton label="Search" icon={<Search />} onClick={onOpenSearch} className="md:hidden" />
        <EnvBadge className="hidden sm:inline-flex" />
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="gap-2 px-1.5 sm:pr-2.5"
              aria-label={`Account menu for ${name}`}
            >
              <Avatar name={name} src={avatarSrc(user)} size="sm" />
              <span className="hidden max-w-[10rem] truncate text-sm font-medium sm:inline">
                {name}
              </span>
              <ChevronDown className="hidden text-subtle-foreground sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <span className="block truncate text-sm font-medium text-foreground">{name}</span>
              {user?.email && (
                <span className="block truncate text-xs font-normal text-muted-foreground">
                  {user.email}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild icon={<UserRound />}>
              <Link to={withProfile(location)}>Profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild icon={<Settings />}>
              <Link to="/settings">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem tone="danger" icon={<LogOut />} onSelect={handleSignOut}>
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
